import React from 'react'
import { Box } from '@hub/box'
import { FlexOptions, Stack as ChakraStack } from '@chakra-ui/react'
import {
  As,
  HubResponsiveValue,
  HubStyleObject,
  StandardSizes,
  StandardSpacings,
  forwardRef,
} from '@hub/design-system-base'

interface StackProps {
  gap?: HubResponsiveValue<StandardSpacings>
  sx?: HubStyleObject
  as?: As
  divider?: React.ComponentProps<typeof ChakraStack>['divider']
  className?: string
  direction?: HubResponsiveValue<'column' | 'row'>
  /**
   * The minimum width of children, merged into the child's `sx` prop.
   */
  minWidth?: HubResponsiveValue<StandardSizes>
  /**
   * The maximum width of children, merged into the child's `sx` prop.
   */
  maxWidth?: HubResponsiveValue<StandardSizes>
  /**
   * Wrap each child in a Hub <Box>. Can be useful when children styles
   * conflict with the styles a <Stack> needs to inject.
   */
  shouldWrapChildren?: boolean
  justify?: FlexOptions['justify']
  align?: FlexOptions['align']
  flexBasis?: FlexOptions['basis']
}

export const Stack = forwardRef<StackProps, typeof ChakraStack>(
  (
    {
      gap = 'spacing-none',
      sx,
      as,
      className,
      children,
      direction = 'column',
      minWidth,
      maxWidth,
      divider,
      shouldWrapChildren = true,
      justify,
      align,
      flexBasis,
      flexGrow,
      flexShrink,
    },
    ref
  ) => (
    <ChakraStack
      ref={ref}
      shouldWrapChildren={shouldWrapChildren}
      as={as}
      spacing={gap}
      direction={direction}
      className={className}
      divider={divider}
      justify={justify}
      align={align}
      sx={sx}
    >
      {React.Children.map(
        children,
        child =>
          React.isValidElement(child) &&
          React.cloneElement(child, {
            sx: {
              minWidth,
              maxWidth,
              ...(direction === 'row'
                ? { ...(flexGrow ?? { flexGrow: 1 }), flexBasis, flexShrink }
                : { flexGrow, flexBasis, flexShrink }),
              ...(child?.props?.sx ?? {}),
            },
          })
      )}
    </ChakraStack>
  )
)

interface StackSpacerProps {
  sx?: HubStyleObject
  as?: As
}

export const StackSpacer: React.FC<StackSpacerProps> = ({
  sx,
  as: AsComp = Box,
}) => (
  // We have to wrap the `as` component to ensure our styles are injected
  // correctly
  <Box
    as={props => (
      <AsComp
        sx={{
          ...sx,
          ...props.sx,
          // Forcibly override Chakra's margins
          marginInlineStart: '0 !important',
          marginLeft: '0 !important',
          marginTop: '0 !important',
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 'auto',
          maxWidth: 'none',
        }}
      />
    )}
  />
)
